<template>
  <div class="video-wrapper">
    <div
      v-if="showPoster"
      class="poster"
      :style="'background-image:' + getImage"
    />
    <video
      v-if="!isWs"
      id="vpjs-video"
      ref="videoPlayer"
      class="video-js fmp-container"
      playsinline
      webkit-playsinline
      x5-video-player-type="h5"
    />
    <!-- 解决端内视频会出现大logo问题 - 方法post变成1px透明图-->
    <video
      v-else
      id="vpjs-video"
      ref="videoPlayer"
      class="video-js fmp-container"
      playsinline
      webkit-playsinline
      x5-video-player-type="h5"
      :poster="transparentImg"
    />
    <div
      class="play-status"
      @click="clickPlayStatus"
    >
      <div
        v-if="showPlayBtn"
        v-show="initPlayBtn && !isPlaying"
        class="btn-wrapper"
      >
        <i class="play-btn">
          <JumpMiniApp
            v-if="miniAppPathClickPlay && showPlayBtn && initPlayBtn && !isPlaying"
            :path="miniAppPathClickPlay"
            @launch="clickPlayStatus"
          />
        </i>
      </div>
      <JumpMiniApp
        v-if="miniAppPathClickPlay && showPlayBtn && initPlayBtn && !isPlaying"
        :path="miniAppPathClickPlay"
        @launch="clickPlayStatus"
      />
    </div>
    <div
      v-if="showMask"
      class="mask"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, inject } from 'vue';
import videojs, { VideoJsPlayer } from 'video.js';
import { IS_SSR } from '@/utils/env';
import { fixHttps } from '@/utils/no-import-meta/url';
import { Browser } from '@/utils/no-import-meta/get-browser-obj';
import { ModelMetaFeed } from '@/types/model';
import {
  SpecMapType,
  STATE_START_PLAY,
  STATE_PAUSED,
  DEFAULT_OPTIONS,
} from '@/constants/video';
import {
  PROVIDED_URL_INFO_NAME,
  TUrlInfo,
} from '@/composable/use-provide-url-info';
import { PROVIDED_BROWSER_INFO_NAME } from '@/composable/use-provide-browser-info';
import useGetCommonMethods from '@/composable/use-get-common-method';
import transparentImg from '@/assets/transparent-1px.png';
import JumpMiniApp from './jump-mini-app.vue';
import 'video.js/dist/video-js.css';

const enum EEmitEvent {
  onPreventAutoplay = 'on-prevent-autoplay',
  onClickPlayStatus = 'on-click-playStatus',
  onFirstPlay = 'on-first-play',
  onError = 'on-error',
  onPlay = 'on-play',
  onPlaying = 'on-playing',
  onPause = 'on-pause',
  onEnded = 'on-ended',
  onTimeUpdate = 'on-timeupdate',
  hideHolder = 'hideHolder',
}

const enum EPlayerEvent {
  play = 'play',
  error = 'error',
  playing = 'playing',
  pause = 'pause',
  ended = 'ended',
  timeupdate = 'timeupdate',
}

export default defineComponent({
  name: 'VideoPlayer',
  components: {
    JumpMiniApp,
  },
  props: {
    miniAppPathClickPlay: {
      type: String,
      default: '',
    },
    playByVisibilityChange: {
      type: Boolean,
      default: true,
    },
    showPlayBtn: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    feed: {
      type: Object as PropType<ModelMetaFeed>,
      default: () => ({}),
    },
    showMask: {
      type: Boolean,
      default: true,
    },
    showPoster: {
      type: Boolean,
      default: true,
    },
    playIconClick: {
      type: Function,
      default: null,
    },
  },
  emits: [
    EEmitEvent.onPreventAutoplay,
    EEmitEvent.onClickPlayStatus,
    EEmitEvent.onFirstPlay,
    EEmitEvent.onError,
    EEmitEvent.onPlay,
    EEmitEvent.onPlaying,
    EEmitEvent.onPause,
    EEmitEvent.onEnded,
    EEmitEvent.onTimeUpdate,
    EEmitEvent.hideHolder,
  ],
  setup() {
    const urlInfo = inject(PROVIDED_URL_INFO_NAME) as TUrlInfo;
    const browerInfo = inject(PROVIDED_BROWSER_INFO_NAME) as Browser;
    const { getSpecUrl, getSrcFromFeed } = useGetCommonMethods();
    return {
      urlInfo,
      browerInfo,
      getSpecUrl,
      getSrcFromFeed,
    };
  },
  data() {
    return {
      player: null as null | VideoJsPlayer,
      initPlayBtn: false,
      state: STATE_PAUSED,
      isAutoPlay: false,
      pauseByVisibilityChange: false,
      onetime: true,
    };
  },
  computed: {
    isPlaying() {
      return this.state === STATE_START_PLAY;
    },
    getImage() {
      return `url(${fixHttps(this.feed?.images?.[0]?.url)})`;
    },
    transparentImg() {
      return transparentImg;
    },
    isWs() {
      return this.browerInfo.client === 'ws';
    },
  },
  watch: {
    feed() {
      if (IS_SSR || !this.player) return;
      // 不要reset 否则会删除webkit-playsinline属性，ios9会全屏播放
      const videoSrcAry = this.getSrcs();
      this.player.src(videoSrcAry[0]);
      this.player.load();
      if (!this.options.changeNotPlay) {
        // ios9上不加setTimeout播放不了，莫名其妙
        setTimeout(() => {
          this.play();
        });
      }
    },
  },
  mounted() {
    this.initVideo();
    this.visibility();
  },
  beforeUnmount() {
    if (this.player) {
      this.player.dispose();
    }
    if (this.playByVisibilityChange) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  },
  methods: {
    initVideo() {
      const { vtype } = this.urlInfo.query || {};
      // query增加interactive时自动播放
      this.isAutoPlay = vtype === 'interactive' ? true : !!this.options.autoPlay;
      const videoSrcAry = this.getSrcs();
      let index = 0;
      const interactiveControls = vtype === 'interactive' ? {
        loop: true, // 循环播放
        controls: true, // 是否显示控制条
        controlBar: { // 设置控制条组件
          /* 使用children的形式可以控制每一个控件的位置，以及显示与否 */
          children: [
            { name: 'playToggle' }, // 播放按钮
            { name: 'currentTimeDisplay' }, // 当前已播放时间
            { name: 'progressControl' }, // 播放进度条
            { name: 'durationDisplay' }, // 总时间
            {
              name: 'volumePanel', // 音量控制
              inline: false, // 不使用水平方式
            },
            { name: 'FullscreenToggle' }, // 全屏
          ],
        },
      } : {};
      const videoOptions = {
        sources: [{ src: videoSrcAry[index] }],
        ...DEFAULT_OPTIONS,
        ...this.options,
        ...interactiveControls,
      };
      // 不传入任何autoPlay的相关属性，采用函数式的autoPlay
      // @ts-ignore
      delete videoOptions.autoPlay;
      const videoPlayer = this.$refs.videoPlayer as HTMLElement;
      this.player = videojs(videoPlayer, videoOptions, () => {
        this.bindEvents();
        this.autoPlay();
      });
      // 这里当前视频加载失败切换其他 source
      videojs.hook('beforeerror', (player, err) => {
        index += 1;
        if (index >= videoSrcAry.length) index = 0;
        // Video.js 在切换/指定 source 后立即会触发一个 err=null 的错误，这里过滤一下
        if (err !== null) {
          player.src(videoSrcAry[index]);
        }

        // 清除错误，避免 error 事件在控制台抛出错误
        return null;
      });
    },
    getSrcs() {
      console.log('&&&&&&&this.feed&&&&&&&&&');
      console.log(this.feed);
      const { vtype } = this.urlInfo.query || {};
      const verify = vtype === 'interactive';
      // 不支持H265时
      if (!window?.MediaSource?.isTypeSupported('video/mp4; codecs="hev1.1.6.L93.B0, mp4a.40.5"')) {
        return [
          this.getSpecUrl(this.feed.videoSpecUrls, 'rec', verify),
          this.getSpecUrl(this.feed.videoSpecUrls, 'f0', verify),
        ];
      }
      const srcAry = [];
      const { spec } = this.urlInfo.query || {};
      // 拿到spec档位对应的视频
      if (spec) {
        const src = this.getSpecUrl(this.feed.videoSpecUrls, spec as SpecMapType, verify);
        if (src) srcAry.push(src);
      }
      srcAry.push(...this.getSrcFromFeed(this.feed, verify));
      return srcAry;
    },
    autoPlay() {
      if (!this.isAutoPlay || this.browerInfo.isQQStudyMode) {
        this.initPlayBtn = true;
        return;
      }
      if (!this.player) return;
      const promise = this.player.play();
      if (promise !== undefined) {
        promise
          .then(() => {
            console.log('Autoplay started!');
          })
          .catch((error) => {
            // 自动播放失败展示播放按钮
            this.initPlayBtn = true;
            this.$emit(EEmitEvent.onPreventAutoplay);
            console.log('Autoplay was prevented.');
            console.warn(error);
          });
      }
    },
    clickPlayStatus() {
      if (this.playIconClick && !this.playIconClick()) {
        return;
      }
      if (this.showPlayBtn) {
        if (this.state === STATE_START_PLAY) {
          this.pause();
        } else {
          this.play();
        }
      }
      this.$emit(EEmitEvent.onClickPlayStatus);
    },
    bindEvents() {
      this.onFirstPlay();
      this.onPlay();
      this.onPlaying();
      this.onPause();
      this.onEnded();
      this.onTimeupdate();
      this.onError();
    },
    onFirstPlay() {
      this.player?.one(EPlayerEvent.play, () => {
        this.$emit(EEmitEvent.onFirstPlay);
      });
    },
    onError() {
      this.player?.on(EPlayerEvent.error, () => {
        this.$emit(EEmitEvent.onError);
        this.initPlayBtn = false;
      });
    },
    onPlay() {
      this.player?.on(EPlayerEvent.play, () => {
        this.$emit(EEmitEvent.onPlay);
        this.initPlayBtn = true;
        this.state = STATE_START_PLAY;
      });
    },
    onPlaying() {
      this.player?.on(EPlayerEvent.playing, () => {
        this.$emit(EEmitEvent.onPlaying, {
          player: this.player,
          feed: this.feed,
        });
        this.onetime = true;
      });
    },
    onPause() {
      this.player?.on(EPlayerEvent.pause, () => {
        this.state = STATE_PAUSED;
        this.$emit(EEmitEvent.onPause);
      });
    },
    onEnded() {
      const { vtype } = this.urlInfo.query || {};
      this.player?.on(EPlayerEvent.ended, () => {
        if (vtype === 'interactive')  return;
        this.$emit(EEmitEvent.onEnded);
      });
    },
    onTimeupdate() {
      this.player?.on(EPlayerEvent.timeupdate, () => {
        if (!this.player) return;
        const progress = Number((this.player.currentTime() / this.player?.duration()) * 100);
        this.$emit(EEmitEvent.onTimeUpdate, Math.round(progress || 0));
        if (this.player?.currentTime() !== 0 && this.onetime) {
          // 视频真正播放时间
          this.onetime = false;
          this.$emit(EEmitEvent.hideHolder);
        }
      });
    },
    play() {
      if (this.player) this.player.play();
    },
    pause() {
      if (this.player) this.player.pause();
    },
    stop() {
      if (this.player) {
        this.player.currentTime(0);
        this.pause();
      }
    },
    visibility() {
      if (!this.playByVisibilityChange) return;
      document.addEventListener('visibilitychange', this.visibilityHandler);
    },
    visibilityHandler() {
      if (
        document.visibilityState === 'hidden'
        && this.state === STATE_START_PLAY
      ) {
        this.pause();
        this.pauseByVisibilityChange = true;
      }
      if (
        document.visibilityState === 'visible'
        && this.pauseByVisibilityChange
      ) {
        this.play();
        this.pauseByVisibilityChange = false;
      }
    },
    // 互动组件获取视频当前时间
    getCurrentTime() {
      if (this.player) {
        return this.player.currentTime();
      }
      return 0;
    },
    // 互动跳转seek组件方法
    seek(time: number, noPlay = false, noPause = false) {
      if (!this.player) {
        return;
      }
      !noPause && this.pause();
      this.player.currentTime(time);
      !noPlay && this.play();
    },
  },
});
</script>

<style lang="scss" scoped>
.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  .poster {
    position: absolute;
    margin: 0;
    padding: 0;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    background-size: contain;
  }
  .play-status {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 50px;
    width: 100%;
    .btn-wrapper {
      position: absolute;
      left: 50%;
      top: 50%;
      @include responsiveStyleSingle(width, 162, "px");
      @include responsiveStyleSingle(height, 188, "px");
      transform: translate3d(-50%, -50%, 0);
      user-select: none;
      z-index: 1000;
      .play-btn {
        display: inline-block;
        background-image: url("../../assets/icon_play_start.png");
        @include responsiveStyleSingle(width, 162, "px");
        @include responsiveStyleSingle(height, 188, "px");
        background-size: 100%;
        background-repeat:no-repeat;
      }
    }
  }
  .mask {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    pointer-events: none;
    @include responsiveStyleSingle(height, 73.3333, "vw");
    background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.4) 100%
    );
  }
  :deep(.video-js) {
    background: transparent;
  }
}
</style>
